import React, { useState, useEffect, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import {
  Form,
  Button,
  Modal,
  Card,
  Row,
  Col,
  Dropdown} from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'

import 'bootstrap/dist/css/bootstrap.min.css'
import { FaClipboardList, FaTimes, FaCheckCircle } from 'react-icons/fa'
import { BsFunnelFill, BsSearch, BsArrowClockwise, BsPencilFill, BsTrashFill, BsThreeDotsVertical, BsCalendar2Week, BsEyeFill } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import EditExamSheet from './EditExamSheet'
import Cookies from 'js-cookie'
import axios from 'axios'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import { Typeahead } from 'react-bootstrap-typeahead'
import { useNavigate } from 'react-router-dom'

/** Renders the action menu on document.body so a short table cannot clip it. */
const PortalDropdownMenu = forwardRef(
  ({ children, style, className, show, close, align, ...props }, ref) =>
    createPortal(
      <div ref={ref} style={style} className={className} {...props}>
        {children}
      </div>,
      document.body,
    ),
)
PortalDropdownMenu.displayName = 'PortalDropdownMenu'

const actionMenuPopperConfig = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-end', 'top-start', 'bottom-start'],
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'offset',
      options: {
        offset: [0, 8],
      },
    },
  ],
}

const DataTable = () => {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const { user } = useAuth()
  const [courses, setCourses] = useState([])

  const [data, setData] = useState([])
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [selectedCourse, setSelectedCourse] = useState()
  const [cancelExam, setCancelExam] = useState(false)
  const [completeExam, setCompleteExam] = useState(false)
  const [blinkColor, setBlinkColor] = useState('red')
  const [selectedCancelInfo, setSelectedCancelInfo] = useState({
    exam_name: '',
    course_id: ''})
  const [selectedCompleteInfo, setSelectedCompleteInfo] = useState({
    exam_name: '',
    course_id: ''})
  const [confirmCheckBox, setConfirmCheckBox] = useState(false)
  const [confirmCompleteCheckBox, setConfirmCompleteCheckBox] = useState(false)
  const [selectedExamId, setSelectedExamId] = useState('')
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewExam, setViewExam] = useState(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkColor((prevColor) => (prevColor === 'red' ? 'blue' : 'red'))
    }, 500)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data))
  }, [])

  const fetchAllExam = async () => {
    try {
      const institueId = Cookies.get('institute_id')

      const response = await axios.get(
        BASE_URL+'/get/exam/all',
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
            'institute-Id': Number(institueId)}},
      )
      console.log('Response from exams: ', response.data.data)
      setSelectedExamId(response.data.data)
      setData(response.data.data)
    } catch (error) {
      console.error(error.response?.data?.message)
    }
  }

  useEffect(() => {
    fetchAllExam()
  }, [])

  const filteredData = data.filter(
    (exam) =>
      (!selectedCourse || Number(exam.course_id) === Number(selectedCourse)) &&
      Object.values(exam).some((val) =>
        val?.toString().toLowerCase().includes(search.trim().toLowerCase()),
      ),
  )

  // const filteredData = data.filter((exam) => exam.course_id === selectedCourse)

  // console.log('Filtered Data:', filteredData)
  const handleCourseChange = (e) => {
    const courseId = e.target.value
    console.log('Selected Course ID: ', courseId)

    setSelectedCourse(courseId)
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredData, totalPages])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem)

  const statusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'text-success fw-semibold'
      case 'upcoming':
        return 'text-primary fw-semibold'
      case 'cancel':
        return 'text-danger fw-semibold'
      default:
        return 'fw-semibold'
    }
  }
  const isClosedExamStatus = (status) => {
    const value = String(status || '').trim().toLowerCase()
    return value === 'complete' || value === 'cancel'
  }
  const formatExamDate = (value) => {
    if (!value) return 'N/A'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'})
  }
  const formatMarkingScheme = (scheme) => {
    if (scheme === 'grade_number') return 'Grade & Number'
    if (scheme === 'grade') return 'Grade'
    if (scheme === 'number') return 'Number'
    return scheme || '—'
  }
  const formatExamStatus = (status) => {
    if (!status) return '—'
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }
  const openExamDetails = (exam) => {
    setViewExam(exam)
    setShowViewModal(true)
  }
  const closeExamDetails = () => {
    setShowViewModal(false)
    setViewExam(null)
  }
  const viewButtonStyle = {
    height: '36px',
    borderRadius: '8px',
    padding: '0 14px',
    border: '1px solid #223843',
    backgroundColor: '#ffffff',
    color: '#223843',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'Inter, system-ui, sans-serif',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0}
  const handleRefresh = () => {
    setSelectedCourse('')
    setSearch('')
    setCurrentPage(1)
    fetchAllExam()
  }
  const handleCancelExam = async (e, examId) => {
    e.preventDefault()
    try {
      console.log('examId', examId)
      const response = await ApiEndpoints.cancelExamById(examId)
      console.log('Response after cancel', response)
      toast.success(response.data.message)
      setCancelExam(false)
    } catch (error) {
      toast.error(error.response?.data?.message)
      setCancelExam(false)
    }
  }

  const handleCompleteExam = async (e, examId) => {
    e.preventDefault()
    try {
      const response = await ApiEndpoints.completeExamById(examId)
      toast.success(response.data.message)
      setCompleteExam(false)
      fetchAllExam()
    } catch (error) {
      toast.error(error.response?.data?.message)
      setCompleteExam(false)
    }
  }
  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }

          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
          .rbt-menu {
            z-index: 1051 !important;
          }
          .filter-card-container {
            overflow: visible !important;
          }

          /* Portaled action menu stays above the page when the table is only one or two rows tall */
          body > .exam-sheet-action-menu {
            z-index: 2000 !important;
          }

          /* Desktop/tablet table: scroll sideways when columns exceed the viewport.
             overflow-y must stay hidden so the horizontal scrollbar is not forced
             on top of the last row's action button when the table is short. */
          @media screen and (min-width: 768px) {
            .exam-sheet-table-scroll {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              overflow-x: auto;
              overflow-y: hidden;
              -webkit-overflow-scrolling: touch;
              padding-bottom: 16px;
            }
            .exam-sheet-table-scroll table {
              width: max-content;
              min-width: 100%;
            }
            .exam-sheet-table-scroll th,
            .exam-sheet-table-scroll td {
              white-space: nowrap;
            }
          }

          .exam-detail-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            padding: 12px 0;
            border-bottom: 1px solid #E5E7EB;
          }
          .exam-detail-row:last-child {
            border-bottom: none;
          }
          .exam-view-modal .modal-dialog {
            margin: 16px auto;
            max-width: 560px;
            width: calc(100% - 32px);
          }
          @media screen and (max-width: 40em) {
            .exam-sheet-table-scroll td.exam-sheet-action-cell > div {
              justify-content: flex-start !important;
              width: 100%;
            }
          }
          @media screen and (max-width: 576px) {
            .exam-view-modal .modal-dialog {
              margin: 8px auto;
              width: calc(100% - 16px);
            }
            .exam-detail-row {
              flex-direction: column;
              gap: 4px;
            }
            .exam-detail-row span:last-child {
              text-align: left !important;
            }
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px'}}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%'}}
          >
            {/* Left: Title and Add Exam Icon Button */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0}}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif'}}
              >
                Manage Exams
              </h3>
              {/* <button
                onClick={() => setShowModal(true)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#223843',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add exam for a course"
              >
                <FaClipboardList size={18} />
              </button> */}
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter Toggle Button and Items Per Page */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page Selector */}
              <div 
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#223843'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D3D4'
                  }}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>

              <button
                type="button"
                className="d-none d-md-flex align-items-center justify-content-center"
                onClick={() => navigate('/ExamSheet/Add')}
                style={{
                  borderRadius: '10px',
                  padding: '6px 10px',
                  height: '44px',
                  border: 'none',
                  backgroundColor: '#223843',
                  color: '#ffffff',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1f323a'
                  e.currentTarget.style.transform = 'scale(1.03)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add exam for a course"
              >
                <FaClipboardList size={18} />
                <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Add exam for a course
                </span>
              </button>
          </div>

          <div className="d-md-none d-flex justify-content-end mb-4">
            <button
              type="button"
              onClick={() => navigate('/ExamSheet/Add')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add exam for a course"
            >
              <FaClipboardList size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add exam for a course
              </span>
            </button>
          </div>

          {/* Modern Filter Section */}
          <div 
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible'}}
              >
                {/* Header */}
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Filter Exams
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Search by course or exam details
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'}}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Course
                        </label>
                        <Typeahead
                          id="course-select"
                          labelKey="name"
                          onChange={(selected) => {
                            const course = selected[0]
                            if (course) {
                              handleCourseChange({ target: { value: course.id } })
                            } else {
                              handleCourseChange({ target: { value: '' } })
                            }
                          }}
                          placeholder="Select Course"
                          options={courses}
                          selected={
                            selectedCourse
                              ? courses.filter((cs) => cs.id === Number(selectedCourse))
                              : []
                          }
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #D1D3D4',
                            fontSize: '14px',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsSearch 
                            style={{ 
                              position: 'absolute', 
                              left: '12px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1}} 
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search exams..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif'}}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>
        {/* Table */}
        <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff', maxWidth: '100%' }}>
          <div className="exam-sheet-table-scroll">
          <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Thead>
              <Tr style={{ backgroundColor: '#F3F4F6' }}>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Course</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Exam Name</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Location</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Session</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Marking Scheme</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Passing Marks</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Total Marks</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Start Date</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>End Date</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Status</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'center', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item, index) => (
                  <Tr 
                    key={index}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                      transition: 'background-color 0.2s ease',
                      borderBottom: '1px solid #E5E7EB'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                    }}
                  >
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {courses.find((course) => course.id === item.course_id)?.name || 'N/A'}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.exam_name}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.exam_location || '—'}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.session}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {item.marking_scheme === 'grade_number'
                        ? 'Grade & Number'
                        : item.marking_scheme === 'grade'
                        ? 'Grade'
                        : item.marking_scheme === 'number'
                        ? 'Number'
                        : ''}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.passing_marks}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.total_marks}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {item.start_date
                        ? new Date(item.start_date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'})
                        : 'N/A'}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {item.end_date
                        ? new Date(item.end_date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'})
                        : 'N/A'}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      <span className={statusColor(item.status)}>
                        {item.status
                          ? item.status.charAt(0).toUpperCase() +
                            item.status.slice(1).toLowerCase()
                          : ''}
                      </span>
                    </Td>
                    <Td className="text-center exam-sheet-action-cell" style={{ padding: '16px', border: 'none' }}>
                      {isClosedExamStatus(item.status) ? (
                      <>
                      <div className="d-none d-md-flex justify-content-center align-items-center">
                        <button
                          type="button"
                          onClick={() => openExamDetails(item)}
                          style={viewButtonStyle}
                        >
                          View
                        </button>
                      </div>
                      <div className="d-md-none d-flex justify-content-start align-items-center">
                        <button
                          type="button"
                          onClick={() => openExamDetails(item)}
                          style={viewButtonStyle}
                        >
                          View
                        </button>
                      </div>
                      </>
                      ) : (
                      <div className="d-flex justify-content-start justify-content-md-center align-items-center gap-2 flex-wrap">
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="light"
                            id={`dropdown-${item.id}`}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: 'transparent',
                              border: '1px solid #D1D3D4',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'}}
                            className="dropdown-toggle-no-caret"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#223843'
                              e.currentTarget.style.borderColor = '#223843'
                              e.currentTarget.style.color = '#ffffff'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.borderColor = '#D1D3D4'
                              e.currentTarget.style.color = '#111827'
                            }}
                          >
                            <BsThreeDotsVertical style={{ color: 'inherit' }} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu
                            as={PortalDropdownMenu}
                            className="exam-sheet-action-menu"
                            renderOnMount
                            popperConfig={actionMenuPopperConfig}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              padding: '8px',
                              minWidth: '180px',
                              zIndex: 2000}}
                          >
                            <Dropdown.Item
                              onClick={() => openExamDetails(item)}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsEyeFill size={18} /> View
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => {
                                setSelectedClass(item)
                                setShowEditModal(true)
                              }}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#10B981'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsPencilFill size={18} /> Edit Exam
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => navigate(`/ExamSheet/schedule/${item.id}`)}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#3B82F6'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsCalendar2Week size={18} /> Subject schedule
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => {
                                const courseName =
                                  courses.find((cs) => cs.id === item.course_id)?.name || ''

                                setConfirmCompleteCheckBox(false)
                                setSelectedCompleteInfo({
                                  id: item.id,
                                  exam_name: item.exam_name,
                                  courseName: courseName})
                                setCompleteExam(true)
                              }}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#10B981'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <FaCheckCircle size={18} /> Complete Exam
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => {
                                const courseName =
                                  courses.find((cs) => cs.id === item.course_id)?.name || ''

                                setConfirmCheckBox(false)
                                setSelectedCancelInfo({
                                  id: item.id,
                                  exam_name: item.exam_name,
                                  courseName: courseName})
                                setCancelExam(true)
                              }}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#EF4444'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <FaTimes size={18} /> Cancel Exam
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                      )}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="11" className="text-center" style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No records found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
          </div>
        </Card>


        {/* Pagination & Total Records */}


                <TablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={filteredData.length}
          itemLabel="exams"
        />
        </div>
      </div>

      {/* Modal for Cancel Exam */}
      <Modal
        show={cancelExam}
        onHide={() => setCancelExam(false)}
        size="lg"
        centered
      >
        <Modal.Header className="bg-danger" closeButton>
          <Modal.Title className="text-white" style={{ letterSpacing: '2px' }}>
            Confirm Cancellation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-semibold mt-4 mb-4" style={{ letterSpacing: '2px' }}>
            You are about to cancel the exam{' '}
            <strong className="text-danger">
              {selectedCancelInfo.exam_name?.toUpperCase()}
            </strong>{' '}
            for the course{' '}
            <strong className="text-danger">
              {selectedCancelInfo.courseName?.toUpperCase()}
            </strong>
            .
          </p>
          <hr />
          {/* <p
            style={{
              color: blinkColor,
              marginTop: '5px',
              marginBottom: '10px',
              letterSpacing: '2px'}}
          >
            This action cannot be undone. Are you sure you want to proceed?
          </p> */}
          <Form.Check
            className="mt-3 mb-3"
            type="checkbox"
            id="confirmCancel"
            checked={confirmCheckBox}
            onChange={(e) => setConfirmCheckBox(e.target.checked)}
            label="This action cannot be undone. Are you sure you want to proceed?"
          />
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setCancelExam(false)
              }}
            >
              Close
            </Button>
            <Button
              variant="danger"
              disabled={!confirmCheckBox}
              onClick={(e) => {
                console.log('Clicked Exam ID:', selectedCancelInfo.id)
                handleCancelExam(e, selectedCancelInfo.id)
              }}
            >
              Cancel Exam
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Modal for Complete Exam */}
      <Modal
        show={completeExam}
        onHide={() => setCompleteExam(false)}
        size="lg"
        centered
      >
        <Modal.Header className="bg-success" closeButton>
          <Modal.Title className="text-white" style={{ letterSpacing: '2px' }}>
            Confirm Completion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-semibold mt-4 mb-4" style={{ letterSpacing: '2px' }}>
            You are about to mark the exam{' '}
            <strong className="text-success">
              {selectedCompleteInfo.exam_name?.toUpperCase()}
            </strong>{' '}
            for the course{' '}
            <strong className="text-success">
              {selectedCompleteInfo.courseName?.toUpperCase()}
            </strong>{' '}
            as complete.
          </p>
          <hr />
          <Form.Check
            className="mt-3 mb-3"
            type="checkbox"
            id="confirmComplete"
            checked={confirmCompleteCheckBox}
            onChange={(e) => setConfirmCompleteCheckBox(e.target.checked)}
            label="This action cannot be undone. Are you sure you want to proceed?"
          />
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setCompleteExam(false)
              }}
            >
              Close
            </Button>
            <Button
              variant="success"
              disabled={!confirmCompleteCheckBox}
              onClick={(e) => {
                handleCompleteExam(e, selectedCompleteInfo.id)
              }}
            >
              Complete Exam
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
      <Modal
        className="exam-view-modal"
        show={showViewModal}
        onHide={closeExamDetails}
        centered
        scrollable
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ backgroundColor: '#223843', borderBottom: 'none' }}
        >
          <Modal.Title
            className="text-white"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '18px',
              fontWeight: 700}}
          >
            Exam Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 20px 16px' }}>
          {viewExam && (
            <div>
              {[
                ['Course', courses.find((course) => course.id === viewExam.course_id)?.name || 'N/A'],
                ['Exam Name', viewExam.exam_name || '—'],
                ['Location', viewExam.exam_location || '—'],
                ['Session', viewExam.session || '—'],
                ['Marking Scheme', formatMarkingScheme(viewExam.marking_scheme)],
                ['Passing Marks', viewExam.passing_marks === 0 || viewExam.passing_marks ? viewExam.passing_marks : '—'],
                ['Total Marks', viewExam.total_marks === 0 || viewExam.total_marks ? viewExam.total_marks : '—'],
                ['Start Date', formatExamDate(viewExam.start_date)],
                ['End Date', formatExamDate(viewExam.end_date)],
                ['Status', formatExamStatus(viewExam.status)],
              ].map(([label, value]) => (
                <div className="exam-detail-row" key={label}>
                  <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: 600, flexShrink: 0 }}>
                    {label}
                  </span>
                  <span
                    className={label === 'Status' ? statusColor(viewExam.status) : undefined}
                    style={{
                      color: label === 'Status' ? undefined : '#111827',
                      fontSize: '14px',
                      fontWeight: 500,
                      textAlign: 'right',
                      wordBreak: 'break-word'}}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #E5E7EB' }}>
          <Button
            variant="secondary"
            onClick={closeExamDetails}
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <EditExamSheet
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        examData={selectedClass}
        refreshExamsData={fetchAllExam}
      />
    </>
  )
}

export default DataTable
